import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Assessment } from '../types/assessment';
import { Bar, BarChart, CartesianGrid, Cell, Label, PieChart, Pie, XAxis, YAxis } from 'recharts';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart';
import ResultsPdfExport from './ResultsPdfExport';
import ExportFooter from '@/components/export-footer';
import ExportHeader from '@/components/export-header';



function capitalizeFirstLetter(val) {
  return String(val)
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}


// Donut chart component showing percentage distribution of maturity levels
const MaturityDonutChart: React.FC<{ assessment: Assessment; colorMap: Record<number, string> }> = ({ assessment, colorMap }) => {
  // Count maturity levels across all categories
  const maturityLevelCounts = { 0: 0, 1: 0, 2: 0, 3: 0 };
  let totalCategories = 0;
  
  // Calculate the distribution of maturity levels
  Object.values(assessment.maturityLevels).forEach(level => {
    const safeLevel = level as 0 | 1 | 2 | 3;
    maturityLevelCounts[safeLevel]++;
    totalCategories++;
  });
  
  // Convert to percentage data for the pie chart
  const chartData = Object.entries(maturityLevelCounts).map(([level, count]) => ({
    name: `Level ${level}`,
    value: (count / totalCategories) * 100,
    color: colorMap[parseInt(level) as 0 | 1 | 2 | 3]
  })).filter(item => item.value > 0); // Only include levels with values > 0

  // Calculate the overall maturity level (minimum across all categories)
  const overallMaturity = Math.min(...Object.values(assessment.maturityLevels));

  // Configure the chart colors for shadcn
  const chartConfig: Record<string, { label: string, color: string }> = {};
  chartData.forEach(data => {
    chartConfig[`level${data.name.replace('Level ', '')}`] = {
      label: data.name,
      color: data.color,
    };
  });

  return (
    <div className="relative">
      <ChartContainer 
        config={chartConfig as ChartConfig}
        className="mx-auto aspect-square w-full max-w-[250px]"
      >
        <PieChart>
          <ChartTooltip
            content={<ChartTooltipContent 
              formatter={(value: any, name) => [`${Math.round(value as number)}%`, name]} 
              className="bg-card"
            />}
          />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            paddingAngle={2}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <ChartLegend
            verticalAlign="bottom"
            align="center"
            layout="horizontal"
            content={
              <ChartLegendContent 
                payload={chartData.map(data => ({
                  value: data.name,
                  color: data.color
                }))}
              />
            }
          />
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) - 10}
                      className="fill-foreground text-3xl font-bold"
                    >
                      {overallMaturity}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) + 16}
                      className="fill-muted-foreground text-xs"
                    >
                      Overall Level
                    </tspan>
                  </text>
                )
              }
              return null;
            }}
          />
        </PieChart>
      </ChartContainer>
    </div>
  );
};

// Custom legend component for maturity levels
const MaturityLevelsLegend: React.FC<{ data: { label: string; value: number; color: string }[] }> = ({ data }) => {
  return (
    <div className="flex flex-wrap justify-center gap-4 mt-4">
      {data.map((item, index) => (
        <div key={index} className="flex items-center">
          <div
            className="w-3 h-3 rounded-sm mr-1.5"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-xs">
            {item.label} (Level {item.value})
          </span>
        </div>
      ))}
    </div>
  );
};

const nameLookup = {
  'patch-applications': 'Patch Apps',
  'patch-operating-systems': 'Patch Systems',
  'mfa': 'MFA',
  'restrict-admin': 'Restrict Admin',
  'application-control': 'App Control',
  'office-macros': 'Restrict Macros',
  'app-hardening': 'App Hardening',
  'backups': 'Backups',
};

// Bar chart component using shadcn UI Bar Chart - Active style
const MaturityBarChart: React.FC<{ data: { category: string; value: number; maxValue: number; color: string }[] }> = ({ data }) => {
  const [chartWidth, setChartWidth] = useState<number | undefined>(undefined);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setChartWidth(window.innerWidth < 768 ? window.innerWidth - 70 : undefined);
    };
    
    // Set initial width
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Transform data for recharts
  const chartData = data.map(item => ({
    name: nameLookup[item.category] || (item.category === 'Mfa' || item.category === 'mfa' ? 'MFA' : item.category.split('-').join(' ').replace(/\b\w/g, l => l.toUpperCase())),
    value: item.value,  // Current value
    maxValue: item.maxValue,  // Maximum value (3)
    fill: item.color, // This will be used in Cell component for custom colors
  }));

  // Configure the chart colors
  const chartConfig = {
    value: {
      label: "Current Level",
      color: "#64748b", // Use a default color, will override with cells
    },
  } satisfies ChartConfig;

  return (
    <div className="h-[300px] w-full overflow-visible">
      <ChartContainer config={chartConfig}>
        <BarChart 
          data={chartData}
          layout="vertical" 
          margin={{ top: 5, right: 25, left: 70, bottom: 5 }}
          barCategoryGap={8}
          width={chartWidth}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis type="number" domain={[0, 3]} tickCount={4} />
          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
          <ChartTooltip 
            content={
              <ChartTooltipContent 
                formatter={(value, name) => [`Current Level: ${value}`]}
                labelFormatter={(label) => `Category: ${label}`}
                className="bg-card"
              />
            }
          />
          
          {/* Current assessment bar - with dynamic colors */}
          <Bar dataKey="value" barSize={16}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
};

interface ResultsOverviewProps {
  assessment: Assessment | null;
}

const ResultsOverview: React.FC<ResultsOverviewProps> = ({ assessment }) => {
  const resultsRef = useRef<HTMLDivElement>(null);
  
  // Apply print-friendly attributes to chart elements when component mounts
  useEffect(() => {
    // hide id=questionnaire-title
    document.getElementById('questionnaire-title')?.classList.add('hidden');
    // Allow time for charts to render
    const timeoutId = setTimeout(() => {
      // Find all SVG elements and apply print-friendly attributes
      if (resultsRef.current) {
        const svgElements = resultsRef.current.querySelectorAll('svg *[fill]');
        svgElements.forEach(el => {
          // Store original fill for reference
          const fill = el.getAttribute('fill');
          if (fill) {
            el.setAttribute('data-original-fill', fill);
            
            // Apply data-attributes for print styles
            if (fill.includes('oklch')) {
              // Determine maturity level based on position/context
              // This is a simplified approach - charts may need more specific handling
              const parent = el.parentElement;
              if (parent && parent.classList.contains('recharts-layer')) {
                // Try to determine which level this represents
                const index = Array.from(parent.children).indexOf(el as Element);
                if (index >= 0) {
                  el.setAttribute('data-level', String(index % 4));
                }
              }
            }
          }
        });
      }
    }, 1000); // Wait for charts to render
    
    return () => clearTimeout(timeoutId);
  }, [assessment]); // Re-run when assessment changes
  
  // Maturity level colors
  const maturityLevelColors = {
    0: '#f87171', // Red
    1: '#fb923c', // Orange 
    2: '#facc15', // Yellow
    3: '#4ade80', // Green
  };

  // Prepare bar chart data
  const barChartData = Object.keys(assessment.maturityLevels).map(categoryId => {
    const level = assessment.maturityLevels[categoryId] || 0;
    return {
      category: categoryId,
      value: level,
      maxValue: 3,
      color: maturityLevelColors[level as 0 | 1 | 2 | 3]
    };
  });

  // Find strengths and priorities
  const sortedCategories = [...barChartData].sort((a, b) => b.value - a.value);
  const strengths = sortedCategories.slice(0, 3).filter(item => item.value >= 2);
  const priorities = sortedCategories.reverse().slice(0, 3).filter(item => item.value < 2);

  return (
    <ResultsPdfExport filename={`essential-eight-assessment-${assessment.questionnaireVersion.version}.pdf`}>
      <ExportHeader/>
      <div ref={resultsRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-full overflow-x-visible mb-4" style={{ WebkitOverflowScrolling: 'touch', minHeight: '-webkit-fill-available' }}>
        {/* Donut Chart for Overall Maturity */}
        <Card className="col-span-1 row-span-1">
          <CardHeader>
            <CardTitle>Overall Essential Eight Health</CardTitle>
            {/* <CardDescription>Distribution of maturity levels</CardDescription> */}
          </CardHeader>
          <CardContent>
            <MaturityDonutChart assessment={assessment} colorMap={maturityLevelColors} />
          </CardContent>
          <CardFooter className="justify-center">
            <div className="flex flex-row gap-3">
              <MaturityLevelsLegend data={[
                { label: "Zero", value: 0, color: maturityLevelColors[0] },
                { label: "One", value: 1, color: maturityLevelColors[1] },
                { label: "Two", value: 2, color: maturityLevelColors[2] },
                { label: "Three", value: 3, color: maturityLevelColors[3] },
              ]} />
            </div>
          </CardFooter>
          {/*we ned to move the following to bottom of the page */}
          <CardFooter className="flex flex-col gap-1 pt-0 text-center text-sm text-muted-foreground">
            {/* <div>Distribution of maturity levels across categories</div> */}
            {/* <div className="text-xs font-medium">Questionnaire version: {assessment.questionnaireVersion.version}</div> */}
          </CardFooter>
        </Card>
        
        {/* Bar Chart for Category Maturity */}
        <Card className="col-span-1 row-span-1">
          <CardHeader>
            <CardTitle>Maturity by Category</CardTitle>
            {/* <CardDescription>Current maturity level for each assessment area</CardDescription> */}
          </CardHeader>
          <CardContent>
            <MaturityBarChart data={barChartData} />
          </CardContent>
        </Card>

        {/* Strengths */}
        <Card>
          <CardHeader>
            <CardTitle>Strengths</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {strengths.length > 0 ? strengths.map((strength, index) => (
                <div key={index} className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-md">
                  <div className="font-medium text-foreground">{nameLookup[strength.category] || (strength.category === 'Mfa' || strength.category === 'mfa' ? 'MFA' : strength.category.split('-').join(' ').replace(/\b\w/g, l => l.toUpperCase()))}</div>
                  <div className="text-sm text-muted-foreground">Level {strength.value} Maturity</div>
                </div>
              )) : (
                <div className="text-sm text-muted-foreground">No strengths identified yet</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Priorities */}
        <Card>
          <CardHeader>
            <CardTitle>Priorities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {priorities.length > 0 ? priorities.map((priority, index) => (
                <div key={index} className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                  <div className="font-medium text-foreground">{nameLookup[priority.category] || (priority.category === 'Mfa' || priority.category === 'mfa' ? 'MFA' : priority.category.split('-').join(' ').replace(/\b\w/g, l => l.toUpperCase()))}</div>
                  <div className="text-sm text-muted-foreground">Level {priority.value} Maturity</div>
                </div>
              )) : (
                <div className="text-sm text-muted-foreground">No priorities identified yet</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="w-full text-center py-2">
        <span className="text-xs font-medium">Questionnaire version: {assessment.questionnaireVersion.version}</span>
      </div>
      <div className="mt-4">
      <ExportFooter />
      </div>
    </ResultsPdfExport>
  );
};

export default ResultsOverview;
